import { coupons } from '~api';
import { Coupon } from '~models';
import i18n from '../i18n';
import { getField, updateField } from 'vuex-map-fields';
import { Handler } from '~services';
import router from '~router';

const SET_COUPON = 'SET_COUPON';
const SET_COUPONS = 'SET_COUPONS';
const RESET_COUPON = 'RESET_COUPON';
const LOADING = 'LOADING';

const state = {
    coupon: { ...new Coupon() },
    coupons: [],
    isLoading: false
};

const actions = {
    /**
     * Reset coupon and  redirect to coupons list
     * @param {function} commit
     */
    cancel({ commit }) {
        commit(RESET_COUPON);
        router.push({ name: 'dashboard.coupons' });
    },

    /**
     * Reset coupon state
     * @param {function} commit
     */
    resetCoupon({ commit }) {
        commit(RESET_COUPON);
    },

    /**
     * Get coupons
     * @param {function} commit
     * @returns {Promise<*>}
     */
    async getCoupons({ commit }) {
        const resp = await coupons.list();

        if (resp.ok) {
            return commit(SET_COUPONS, resp.data.data);
        }

        return Handler.handleResponse(resp);
    },

    /**
     * Create Coupons
     * @param {function} commit
     * @param {object} state
     */
    async create({ commit, state }) {
        const successMessage = i18n.t('coupons.create.successMessage');

        commit(LOADING);

        const couponParams = Object.keys(state.coupon)
            .filter(e => state.coupon[e] !== null && state.coupon[e].length > 0)
            .reduce((o, e) => {
                o[e] = state.coupon[e];
                return o;
            }, {});

        // Api request to create business
        const resp = await coupons.create(couponParams);

        if (resp.ok) {
            commit(RESET_COUPON);
        }

        // Handle response
        Handler.handleResponse(resp, successMessage, '', 'dashboard.coupons');

        commit(LOADING);
    },

    /**
     * Get Coupon
     * @param {function} commit
     * @param  {string} id
     */
    async read({ commit }, id) {
        commit(LOADING);
        // Api request get business account by id
        const resp = await coupons.read(id);

        if (resp.ok) {
            commit(SET_COUPON, resp.data);
        } else {
            // Handle response if error
            Handler.handleResponse(resp);
        }

        commit(LOADING);

        return resp.ok;
    },

    /**
     * Update coupon
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async update({ commit, state }) {
        const successMessage = i18n.t('coupons.edit.successMessage');

        let { id, ...rest } = state.coupon;

        commit(LOADING);

        const resp = await coupons.update(id, rest);

        Handler.handleResponse(resp, successMessage, '', 'dashboard.coupons');

        // Reset store business if account deleted
        if (resp.ok) {
            commit(RESET_COUPON);
        }

        commit(LOADING);

        return resp.ok;
    },

    /**
     * Delete Coupon.
     * @param {function} commit
     * @param {function} dispatch
     * @param {object} state
     * @returns {Promise<void>}
     */
    async delete({ commit, dispatch }, id) {
        const successMessage = i18n.t('coupons.list.deleteSuccessMessage');

        // Show confirm modal window
        const confirmed = await dispatch(
            'prompt/show',
            i18n.t('prompt.delete'),
            { root: true }
        );
        if (!confirmed) return;

        commit(LOADING);

        // Api request on delete account
        const resp = await coupons.delete(id);

        // Handle response
        Handler.handleResponse(resp, successMessage);

        // Reset store business if account deleted
        if (resp.ok) {
            commit(RESET_COUPON);
        }

        commit(LOADING);

        return resp.ok;
    }
};

const mutations = {
    // Add the `updateField` mutation to the
    // `mutations` of your Vuex store instance.
    updateField,
    [SET_COUPON](state, data) {
        state.coupon = data;
    },
    [SET_COUPONS](state, data) {
        state.coupons = data;
    },
    [RESET_COUPON](state) {
        state.coupon = { ...new Coupon() };
    },
    [LOADING](state) {
        state.isLoading = !state.isLoading;
    }
};

const getters = {
    // Add the `getField` getter to the
    // `getters` of your Vuex store instance.
    getField,
    isLoading: state => state.isLoading,
    coupons: state => state.coupons
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
